/**
 * useProjectOperations Hook
 * Extracted from MultiProjectContext.tsx
 * Handles project CRUD operations (create, refresh)
 */

import { useCallback } from 'react';
import { projectService, ProjectInfo } from '@/features/projects/services/projectService';
import { transformProjectInfoFromBackend } from '@/shared/utils/fieldNameTransform';
import { log } from '@/shared/utils/logger';
import type { ProjectDataWithMeta } from '../types/multiProject';

interface UseProjectOperationsParams {
  projectsData: Map<string, ProjectDataWithMeta>;
  setProjectsData: (updater: (prev: Map<string, ProjectDataWithMeta>) => Map<string, ProjectDataWithMeta>) => void;
  setAvailableProjects: (updater: (prev: ProjectInfo[]) => ProjectInfo[]) => void;
  loadProjectData: (projectId: string, options?: { forceReload?: boolean; shouldApply?: () => boolean }) => Promise<ProjectDataWithMeta>;
  selectProjects: (projectIds: string[], mode?: 'toggle' | 'set' | 'add') => Promise<void>;
}

export function useProjectOperations({
  setProjectsData,
  setAvailableProjects,
  loadProjectData,
  selectProjects
}: UseProjectOperationsParams) {

  // Refresh project data (force reload from server)
  const refreshProject = useCallback(async (projectId: string, options?: { shouldApply?: () => boolean }) => {
    log.info('MultiProject', '🔄 Force refresh project:', projectId);
    log.info('MultiProject', '🔄 Preserving current cache while reloading authoritative project data');

    setProjectsData(prev => {
      const next = new Map(prev);
      const existingData = next.get(projectId);
      log.debug('MultiProject', '🔄 Refreshing cached data in place:', {
        projectId,
        hadData: !!existingData,
        existingNodeCount: existingData?.nodes?.length || 0
      });
      return next;
    });

    // Reload from server
    log.info('MultiProject', '🔄 Reloading project data from server...');
    await loadProjectData(projectId, { forceReload: true, ...options });
    log.info('MultiProject', '🔄 Project refresh complete');
  }, [setProjectsData, loadProjectData]);

  // Create new project
  const createProject = useCallback(async (name: string, description?: string) => {
    const newProject = await projectService.createProject({
      project_name: name,
      description
    });

    // Transform the new project to ensure consistent field names
    const transformedProject = transformProjectInfoFromBackend(newProject);
    if (!transformedProject?.projectId) {
      throw new Error('Project created but response did not include a project ID');
    }

    // Add to available projects
    setAvailableProjects(prev => {
      const existingIndex = prev.findIndex(project => project.projectId === transformedProject.projectId);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          ...transformedProject,
        };
        return next;
      }
      return [...prev, transformedProject];
    });

    // Select the new project
    await selectProjects([transformedProject.projectId]);
  }, [setAvailableProjects, selectProjects]);

  return {
    refreshProject,
    createProject
  };
}
